import { type FC } from "react";
import { Loader2Icon, CheckCircle2Icon, SearchIcon, XCircleIcon } from "lucide-react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";

const SearchUnstructuredContextToolCallComponent: FC<{
  toolName: string;
  argsText: string;
  result?: unknown;
  status?: { type: "running" | "complete" | "incomplete"; reason?: string };
  args?: any;
  toolCallId?: string;
  [key: string]: any;
}> = ({ argsText, result, status }) => {
  const isRunning = status?.type === "running" || (status === undefined && result === undefined);
  const isError = status?.type === "incomplete" && status?.reason === "error";
  const isComplete = status?.type === "complete" && !isError;

  // Parse args to get the search query
  let query = "";
  try {
    const parsedArgs = typeof argsText === "string" ? JSON.parse(argsText) : argsText;
    query = parsedArgs?.query || "";
  } catch {
    const match = typeof argsText === "string" ? argsText.match(/"query"\s*:\s*"([^"]+)"/) : null;
    if (match) query = match[1];
  }

  // Parse result to get found documents
  let documents: { id: string; title: string; keywords?: string[] }[] = [];
  let errorMessage = "";
  if (result !== undefined) {
    try {
      const parsed = typeof result === "string" ? JSON.parse(result) : result;
      documents = parsed?.documents || [];
      errorMessage = parsed?.error || "";
    } catch {
      // ignore parse errors
    }
  }

  const hasError = isError || !!errorMessage;

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning ? "bg-primary/10" : hasError ? "bg-destructive/10" : "bg-muted/50"
      )}>
        <div className="flex items-center gap-2">
          {isRunning ? (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                {query ? (
                  <>Searching documents for <strong>{query}</strong>...</>
                ) : (
                  <>Listing available documents...</>
                )}
              </p>
            </div>
          ) : hasError ? (
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive font-medium">
                {errorMessage || "Failed to search documents"}
              </p>
            </div>
          ) : isComplete ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <SearchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <p className="text-sm m-0 text-muted-foreground">
                  {query
                    ? <>Searched documents for <strong className="text-foreground">{query}</strong></>
                    : <>Listed available documents</>
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  {documents.length === 0
                    ? "No matching documents found."
                    : `Found ${documents.length} document${documents.length !== 1 ? "s" : ""}`}
                </p>
              </div>
              {documents.length > 0 && (
                <div className="ml-6 mt-1 space-y-1">
                  {documents.map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-foreground font-medium">{doc.title}</span>
                      {(doc.keywords || []).slice(0, 3).map((kw, i) => (
                        <Badge key={i} variant="secondary" className="text-[10px] py-0 px-1.5">
                          {kw}
                        </Badge>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">{query ? 'Searching documents...' : 'Listing available documents...'}</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export const SearchUnstructuredContextToolCall = SearchUnstructuredContextToolCallComponent as React.ComponentType<any>;
